/**
 * CitationsLayer — wrapper que detecta clicks en `<cite data-ref-number>`
 * y muestra un popover con los datos de la cita (filename, página,
 * preview, source path).
 *
 * Implementa el "click → ver fuente" del ADR-0001 Tier 1. El render
 * del contenido es transparente (el wrapper no toca el DOM interno);
 * la única magia es event delegation desde el wrapper hacia los `cite`
 * generados por `CitationMark`.
 *
 * Lookup por `refNumber` contra el array de citas. Si el lookup falla
 * (cita huérfana porque el draft fue editado manualmente y se cambió
 * el `[N]`), el popover muestra un fallback "Cita no disponible".
 *
 * OT-0049 — el popover muestra además cada oración que usa la cita, con la
 * frase literal de la fuente y si quedó respaldada.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, FileText, X } from 'lucide-react'

import type { CitationClaim, CitationRead } from '../../api/citations'

interface CitationsLayerProps {
  citations: CitationRead[]
  /** Filtra citas a solo las de una sección/instancia específica. Si
   * no se pasa, todas las citas del array están disponibles
   * (útil cuando el wrapper envuelve el árbol completo del artifact). */
  sectionId?: string
  instanceKey?: string
  children: ReactNode
}

interface PopoverState {
  refNumber: number
  citation: CitationRead | null
  anchorRect: DOMRect
}

export function CitationsLayer({
  citations,
  sectionId,
  instanceKey,
  children,
}: CitationsLayerProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [popover, setPopover] = useState<PopoverState | null>(null)

  // Filtrado opcional por sección — lo usa el SectionTreeRenderer cuando
  // hay múltiples secciones con `[1]` cada una. Sin filtro, `[1]` de la
  // sección A podría resolver a la cita de la sección B.
  const scopedCitations =
    sectionId && instanceKey
      ? citations.filter(
          (c) =>
            c.section_id === sectionId && c.instance_key === instanceKey,
        )
      : citations

  // Event delegation — un solo listener para todas las `<cite>`.
  useEffect(() => {
    const root = wrapperRef.current
    if (!root) return
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const cite = target.closest('cite[data-ref-number]') as
        | HTMLElement
        | null
      if (!cite || !root.contains(cite)) return
      const raw = cite.getAttribute('data-ref-number')
      const refNumber = raw === null ? NaN : Number.parseInt(raw, 10)
      if (!Number.isFinite(refNumber) || refNumber <= 0) return
      event.preventDefault()
      const citation =
        scopedCitations.find((c) => c.ref_number === refNumber) ?? null
      setPopover({
        refNumber,
        citation,
        anchorRect: cite.getBoundingClientRect(),
      })
    }
    root.addEventListener('click', handler)
    return () => root.removeEventListener('click', handler)
  }, [scopedCitations])

  // Cerrar al click fuera o Escape.
  useEffect(() => {
    if (!popover) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopover(null)
    }
    const onMouse = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest('[data-citation-popover]')) return
      if (target.closest('cite[data-ref-number]')) return
      setPopover(null)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onMouse)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onMouse)
    }
  }, [popover])

  return (
    <div ref={wrapperRef} className="relative">
      {children}
      {popover ? (
        <CitationPopover
          state={popover}
          onClose={() => setPopover(null)}
        />
      ) : null}
    </div>
  )
}

// ─── Popover ──────────────────────────────────────────────────────────────
function CitationPopover({
  state,
  onClose,
}: {
  state: PopoverState
  onClose: () => void
}) {
  const { t } = useTranslation('ai')
  const { refNumber, citation, anchorRect } = state
  // Alineado al borde izquierdo del cite; si se sale por la derecha de la
  // viewport, al borde derecho. Abre del lado (abajo o arriba del cite) con
  // más espacio y nunca más alto que ese espacio: con la evidencia el
  // contenido puede ser largo y se desplaza dentro del popover.
  const POPOVER_W = 360
  const margin = 8
  const gap = 6
  let left = anchorRect.left
  if (left + POPOVER_W > window.innerWidth - margin) {
    left = Math.max(margin, window.innerWidth - POPOVER_W - margin)
  }
  const spaceBelow = window.innerHeight - anchorRect.bottom - gap - margin
  const spaceAbove = anchorRect.top - gap - margin
  const placement =
    spaceBelow >= spaceAbove
      ? { top: anchorRect.bottom + gap, maxHeight: spaceBelow }
      : { bottom: window.innerHeight - anchorRect.top + gap, maxHeight: spaceAbove }

  return (
    <div
      data-citation-popover
      role="dialog"
      aria-label={t('citations.popover_aria_label', {
        ref: refNumber,
      })}
      className="fixed z-50 overflow-y-auto rounded-md border border-line bg-surface shadow-lg p-3"
      style={{ ...placement, left, width: POPOVER_W }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-mono text-[10px] text-brand">
          [{refNumber}]
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-subtle hover:text-ink"
          aria-label={t('citations.popover_close')}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
      {citation === null ? (
        <p className="text-xs text-ink-subtle">
          {t('citations.popover_not_found')}
        </p>
      ) : (
        <div className="text-xs text-ink space-y-1.5">
          <div className="flex items-center gap-2">
            <FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate font-medium">
              {citation.filename ?? citation.source_id ?? '—'}
            </span>
            {citation.page_number !== null && (
              <span className="text-ink-subtle">
                {t('citations.page_label', {
                  page: citation.page_number,
                })}
              </span>
            )}
          </div>
          {citation.section_path.length > 0 && (
            <p className="text-ink-subtle">
              {citation.section_path.join(' • ')}
            </p>
          )}
          {citation.preview ? (
            <p className="text-ink-muted leading-snug line-clamp-4 italic">
              "{citation.preview}"
            </p>
          ) : null}
          <CitationEvidence evidence={citation.evidence} />
          {citation.rerank_score !== null ? (
            <p className="text-[10px] text-ink-subtle font-mono">
              {t('citations.popover_score', {
                score: citation.rerank_score.toFixed(2),
              })}
            </p>
          ) : null}
          {/* Slot para "Abrir fuente" — quedará habilitado cuando el
              source viewer (AI-1.e) llegue. Por ahora deshabilitado
              para no prometer algo que no funciona. */}
          <button
            type="button"
            disabled
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-subtle opacity-50 cursor-not-allowed"
            title={t('citations.popover_open_source_disabled_tooltip')}
          >
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            {t('citations.popover_open_source')}
          </button>
        </div>
      )}
    </div>
  )
}

/** Oraciones que usan la cita, con su frase y estado (OT-0049). Una cita
 * anterior al registro de evidencia (`null`) lo dice en vez de callar. */
function CitationEvidence({ evidence }: { evidence: CitationClaim[] | null }) {
  const { t } = useTranslation('ai')
  if (evidence === null) {
    return <p className="text-[11px] text-ink-subtle">{t('citations.not_recorded')}</p>
  }
  if (evidence.length === 0) return null
  return (
    <div className="space-y-2 border-t border-line pt-2">
      <p className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {t('citations.evidence_heading')}
      </p>
      <ul className="space-y-2">
        {evidence.map((item, index) => (
          <li key={`${index}-${item.claim}`} className="space-y-1">
            <span
              className={`inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${
                item.status === 'verified'
                  ? 'bg-official-soft text-official'
                  : 'bg-review-soft text-review'
              }`}
            >
              {item.status === 'verified'
                ? t('citations.status_verified')
                : t('citations.status_unverified')}
            </span>
            <p className="text-ink">{item.claim}</p>
            {item.quote ? (
              <p className="text-ink-muted italic">
                <span className="sr-only">{t('citations.quote_label')}: </span>
                "{item.quote}"
              </p>
            ) : null}
            {item.reason ? (
              <p className="text-[11px] text-review">{t(`citations.reason.${item.reason}`)}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
